import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CourseProgressResponse,
	CourseProgressResponseSchema,
	InviteCodes,
	InviteCodesSchema,
	StartCourseProgress,
	UploadedFilesSchema,
	VoiceChatTokenSchema,
} from "../../pages/Course/schema/course-progress";
import {
	AcceptInvitationRequest,
	AcceptInvitationRequestSchema,
	AcceptInvitationResponseSchema,
	AcceptInvitationResponse,
	CheckIfAlreadyExistRequest,
} from "../../pages/Course/schema/invite-users";
import {
	CoursesProgressResponse,
	CoursesProgressResponseSchema,
} from "../../pages/Analytics/schema/course-progress";
import { ExportScenarioReport } from "../../pages/Course/schema/export-report";
import { OtpAuthResponse, OtpAuthType } from "../../pages/Course/schema/otp-auth";

export const coursesProgressApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCourseProgresses: builder.query<CoursesProgressResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "course-progress",
				params: {
					...(params && { ...params }),
				},
				responseSchema: CoursesProgressResponseSchema,
			}),
			providesTags: ["CourseProgress"],
		}),
		checkIfScenarioInProgressExist: builder.query<CourseProgressResponse, void>({
			query: () => ({
				url: "course-progress/check-scenario-in-progress",
				responseSchema: CoursesProgressResponseSchema,
			}),
			providesTags: ["CourseProgress"],
		}),
		startScenario: builder.mutation<
			Partial<CourseProgressResponse> & {
				defaultParticipants?: {
					id: string;
					firstName: string;
					lastName: string;
					otpCode: number;
				}[];
			},
			StartCourseProgress
		>({
			query: (body) => {
				return {
					url: `course-progress/start`,
					method: "POST",
					body,
				};
			},
			invalidatesTags: ["CourseProgress"],
		}),

		sendUploadedFiles: builder.mutation<UploadedFilesSchema, FormData>({
			query: (formData) => {
				return {
					url: `course-progress/upload-temp`,
					method: "POST",
					body: formData,
				};
			},
		}),
		removeUploadedFile: builder.mutation({
			query: (uploadedFileId: number) => ({
				url: `/course-progress/upload-temp/${uploadedFileId}`,
				method: "DELETE",
			}),
		}),

		removeAllUploadedFiles: builder.mutation<UploadedFilesSchema, number[]>({
			query: (body) => {
				return {
					url: `course-progress/upload-temp`,
					method: "DELETE",
					body: {
						deleteFileIds: body,
					},
				};
			},
		}),
		getScenarioProgress: builder.query<CourseProgressResponse, string>({
			query: (id: string) => ({
				url: `course-progress/${id}`,
				params: {
					id,
				},
				responseSchema: CourseProgressResponseSchema,
			}),
		}),
		// The run's PINs live on the server; asked for each time "Team einladen" opens, so they
		// are there on any device and always belong to the run shown (BCM360-350).
		getInviteCodes: builder.query<InviteCodes, string>({
			query: (id: string) => ({
				url: `course-progress/${id}/invite-codes`,
				responseSchema: InviteCodesSchema,
			}),
			keepUnusedDataFor: 0,
		}),
		getVoiceChatToken: builder.query<
			VoiceChatTokenSchema,
			{ courseProgressId: string; userId: string | number; userName: string }
		>({
			query: ({ courseProgressId, userId, userName }) => ({
				url: `course-progress/voice-chat/token?roomName=${courseProgressId}&userName=${userName}&userId=${userId}`,
				responseSchema: VoiceChatTokenSchema,
			}),
		}),
		acceptCourseProgressInvitation: builder.mutation<
			AcceptInvitationResponse,
			AcceptInvitationRequest
		>({
			query: (body) => ({
				url: "course-progress/accept-invite",
				method: "POST",
				body,
				headers: {
					authorization: `Bearer ${body.accessToken}`,
				},
				requestSchema: AcceptInvitationRequestSchema,
				responseSchema: AcceptInvitationResponseSchema,
			}),
		}),
		checkIfAlreadyAccepted: builder.mutation<AcceptInvitationResponse, CheckIfAlreadyExistRequest>({
			query: (body) => ({
				url: `course-progress/check-invitation`,
				method: "POST",
				body,
				headers: {
					authorization: `Bearer ${body.accessToken}`,
				},
				responseSchema: AcceptInvitationResponseSchema,
			}),
		}),
		deleteScenarioProgress: builder.mutation<void, string>({
			query: (id) => ({
				url: `course-progress/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["CourseProgress"],
		}),
		exportCourseProgress: builder.mutation<Blob, ExportScenarioReport>({
			query: ({ id, fileType, format }) => ({
				url: `course-progress/report/${id}`,
				method: "POST",
				body: { fileType, format },
				responseHandler: async (response) => response.blob(),
			}),
		}),
		authorizeInScenarioProgressViaOtp: builder.mutation<OtpAuthResponse, OtpAuthType>({
			query: (body) => ({
				url: `course-progress/authorize`,
				method: "POST",
				body,
			}),
		}),
	}),
});

export const {
	useGetScenarioProgressQuery,
	useGetInviteCodesQuery,
	useStartScenarioMutation,
	useAcceptCourseProgressInvitationMutation,
	useCheckIfAlreadyAcceptedMutation,
	useGetCourseProgressesQuery,
	useDeleteScenarioProgressMutation,
	useExportCourseProgressMutation,
	useLazyCheckIfScenarioInProgressExistQuery,
	useAuthorizeInScenarioProgressViaOtpMutation,
	useRemoveUploadedFileMutation,
	useSendUploadedFilesMutation,
	useRemoveAllUploadedFilesMutation,
	useGetVoiceChatTokenQuery,
} = coursesProgressApi;
export const accountApiReducer = coursesProgressApi.reducer;
export const accountApiMiddleware = coursesProgressApi.middleware;
